import { Spacing } from '@persona-id/design-tokens';
import { PlusIcon } from '@persona-id/design-tokens/icons';
import { useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';

import ContextMenu from 'dashboard/components/ContextMenu';
import DisabledProductFeature from 'dashboard/components/DisabledProductFeature';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import NavBack from 'dashboard/components/NavBack';
import Permissioned from 'dashboard/components/Permissioned';
import FormAddTransactionStatus, {
  FormAddTransactionStatusData,
} from 'dashboard/forms/FormAddTransactionStatus';
import FormRenameTransactionStatus, {
  FormRenameTransactionStatusData,
} from 'dashboard/forms/FormRenameTransactionStatus';
import { Container } from 'dashboard/layouts/IndexViewLayoutV2';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import { Backdrop, FullScreenHeaderContainer } from 'dashboard/layouts/ShowViewLayoutV2';
import TableLayout, {
  TableActionTD,
  TableNullState,
  TableTD,
  TableTH,
  TableTR,
} from 'dashboard/layouts/TableLayout';
import { TransactionStatus, TransactionStatusState } from 'dashboard/models/transaction-status';
import { TransactionType } from 'dashboard/models/transaction-type';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Sticker from 'design-system/components/Sticker';
import IconTransactions from 'images/icons/accounts.svg';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { Permission } from 'lib/permissions';

const HorizontalContainer = styled.div`
  display: flex;
  align-items: center;
`;

const BackContainer = styled('div')`
  display: flex;
  align-items: center;
`;

const StyledSticker = styled(Sticker)`
  margin-left: ${Spacing[2]};
`;

interface DashboardTransactionStatusesViewProps {
  transactionStatuses: TransactionStatus[];
  transactionType: TransactionType;
  handleAddTransactionStatus: (name: string, transactionTypeId: string) => Promise<void>;
  handleDeactivateTransactionStatus: (transactionStatusId: string) => Promise<void>;
  handleRenameTransactionStatus: (name: string, transactionStatusId: string) => Promise<void>;
  handleActivateTransactionStatus: (transactionStatusId: string) => Promise<void>;
  handleSetDefaultTransactionStatus: (transactionStatusId: string) => Promise<void>;
  productFeatureIsDisabled: boolean;
}

const DashboardTransactionStatusesView = ({
  transactionStatuses,
  transactionType,
  handleAddTransactionStatus,
  handleDeactivateTransactionStatus,
  handleRenameTransactionStatus,
  handleActivateTransactionStatus,
  handleSetDefaultTransactionStatus,
  productFeatureIsDisabled,
}: DashboardTransactionStatusesViewProps) => {
  const selectedTransactionStatus = useRef(null);
  const [
    addTransactionStatusModal,
    { enable: showAddTransactionStatusModal, disable: hideAddTransactionStatusModal },
  ] = useToggle(false);

  const [
    renameTransactionStatusModal,
    { enable: showRenameTransactionStatusModal, disable: hideRenameTransactionStatusModal },
  ] = useToggle(false);

  const handleAddTransactionStatusAndClose = async (values: FormAddTransactionStatusData) => {
    await handleAddTransactionStatus(values.name, transactionType.id);
    hideAddTransactionStatusModal();
  };

  const handleRenameTransactionStatusAndClose = async (values: FormRenameTransactionStatusData) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    await handleRenameTransactionStatus(values.name, selectedTransactionStatus.current.id);
    hideRenameTransactionStatusModal();
  };

  const hasPermission = usePermission(Permission.TransactionTypeView);
  if (productFeatureIsDisabled || !hasPermission) {
    return (
      <>
        <DisabledProductFeature
          title='Transactions'
          prompt='Transactions mirror interactions in your system.'
          helpLink={
            <a
              href='https://support.withpersona.com/hc/en-us/categories/360004567574-Product-guide'
              target='_blank'
              rel='noreferrer'
            >
              Learn more about transactions
            </a>
          }
          icon={<IconTransactions />}
          popupMessage={
            productFeatureIsDisabled
              ? 'Your current plan does not have access to this feature.'
              : 'Your current role does not have permissions for this feature'
          }
        />
      </>
    );
  }

  return (
    <>
      <Helmet title={`Statuses - ${transactionType.attributes.name} - Types - Transactions}`} />
      <ModalWrapper
        isOpen={addTransactionStatusModal}
        handleCloseModal={() => hideAddTransactionStatusModal()}
      >
        <ModalContainer>
          <ModalHeader onClose={() => hideAddTransactionStatusModal()}>Create Status</ModalHeader>

          <ModalBody>
            <FormAddTransactionStatus
              handleCancel={hideAddTransactionStatusModal}
              onSubmit={handleAddTransactionStatusAndClose}
            />
          </ModalBody>
        </ModalContainer>
      </ModalWrapper>
      <ModalWrapper
        isOpen={renameTransactionStatusModal}
        handleCloseModal={() => hideRenameTransactionStatusModal()}
      >
        <ModalContainer>
          <ModalHeader onClose={() => hideRenameTransactionStatusModal()}>
            Rename Status
          </ModalHeader>

          <ModalBody>
            <FormRenameTransactionStatus
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              initialValues={{ name: selectedTransactionStatus.current?.attributes?.name }}
              handleCancel={hideRenameTransactionStatusModal}
              onSubmit={handleRenameTransactionStatusAndClose}
            />
          </ModalBody>
        </ModalContainer>
      </ModalWrapper>
      <FullScreenHeaderContainer>
        <HorizontalContainer>
          <BackContainer>
            <NavBack to={`/dashboard/transaction-types/${transactionType.id}`}>
              {transactionType.attributes.name} Custom Statuses
            </NavBack>
          </BackContainer>
        </HorizontalContainer>
      </FullScreenHeaderContainer>
      <Container>
        <Backdrop>
          <Card>
            <Card.Body>
              <Card.Title
                actions={
                  <Permissioned permission={Permission.TransactionTypeView} fallback={null}>
                    <Button
                      aria-label='Add new status'
                      id='transaction-statuses-view__add-status-button'
                      variant='basic'
                      prefixIcon={<PlusIcon />}
                      onClick={showAddTransactionStatusModal}
                    >
                      Add Status
                    </Button>
                  </Permissioned>
                }
              >
                Custom Statuses
              </Card.Title>
              <TableLayout
                showHeader={true}
                dataTest='transaction-statuses-view__table-layout'
                headerTR={
                  <>
                    <TableTH>Name</TableTH>
                    <TableTH>{/* Actions */}</TableTH>
                  </>
                }
                nullState={
                  <TableNullState dataTestBlock='transaction-statuses-view'>
                    <h3>Looks like there aren&#39;t any transaction statuses yet.</h3>
                    <p>Create an transaction status to get started.</p>
                  </TableNullState>
                }
              >
                {transactionStatuses.map((transactionStatus: TransactionStatus) => {
                  const isDefault =
                    transactionStatus.id ===
                    transactionType.relationships['default-status'].data.id;
                  const isInactive =
                    transactionStatus.attributes['status'] === TransactionStatusState.Inactive;
                  const contextMenuItems = [
                    {
                      id: 'rename',
                      onClick: () => {
                        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                        selectedTransactionStatus.current = transactionStatus;
                        showRenameTransactionStatusModal();
                      },
                      hidden: false,
                      label: 'Rename',
                    },
                  ];
                  if (!isDefault) {
                    if (isInactive) {
                      contextMenuItems.push({
                        id: 'activate',
                        onClick: () => {
                          handleActivateTransactionStatus(transactionStatus.id);
                        },
                        hidden: false,
                        label: 'Activate',
                      });
                    } else {
                      contextMenuItems.push({
                        id: 'set-as-default',
                        onClick: () => {
                          handleSetDefaultTransactionStatus(transactionStatus.id);
                        },
                        hidden: false,
                        label: 'Set as Default',
                      });
                      contextMenuItems.push({
                        id: 'deactivate',
                        onClick: () => {
                          handleDeactivateTransactionStatus(transactionStatus.id);
                        },
                        hidden: false,
                        label: 'Deactivate',
                      });
                    }
                  }

                  return (
                    <TableTR key={transactionStatus.id}>
                      <TableTD>
                        {transactionStatus.attributes.name}{' '}
                        {isDefault && <StyledSticker>Default</StyledSticker>}
                        {isInactive && <StyledSticker>Inactive</StyledSticker>}
                      </TableTD>
                      <TableActionTD>
                        <Permissioned permission={Permission.TransactionTypeWrite} fallback={null}>
                          <ContextMenu
                            aria-label='More'
                            items={contextMenuItems}
                            renderItem={(item) => (
                              <ContextMenu.Item
                                key={item.id}
                                id={item.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                                  item.onClick();
                                }}
                              >
                                {item.label}
                              </ContextMenu.Item>
                            )}
                          />
                        </Permissioned>
                      </TableActionTD>
                    </TableTR>
                  );
                })}
              </TableLayout>
            </Card.Body>
          </Card>
        </Backdrop>
      </Container>
    </>
  );
};

export default DashboardTransactionStatusesView;
